import { useEffect } from "react";

import { activeBeat, markerNameAt, type BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { PlaybackState } from "@/lib/timeline/playback";

interface TimelineMarkerNamesProps {
  canvas: HTMLCanvasElement | null;
  layout: BookTimelineLayout;
  getPlayback: () => PlaybackState;
  spoilersOn: boolean;
}

/** Keeps the labels of people whose name depends on the chapter or on spoilers current. */
export function TimelineMarkerNames({
  canvas,
  layout,
  getPlayback,
  spoilersOn,
}: TimelineMarkerNamesProps) {
  useEffect(() => {
    if (canvas === null) {
      return;
    }
    const changing = layout.markers.filter((marker) =>
      marker.laterNames !== undefined || marker.revealedNames !== undefined);
    if (changing.length === 0) {
      return;
    }
    const onFrame = (): void => {
      const { playheadMs } = getPlayback();
      const chapterId = activeBeat(layout, playheadMs)?.chapterId;
      const sceneReached = (sceneId: string): boolean => (
        (layout.beats.find((beat) => beat.sceneId === sceneId)?.startMs ?? 0) <= playheadMs
      );
      for (const marker of changing) {
        const label = document.querySelector(
          `[data-marker-part="name"][data-character-id="${CSS.escape(marker.characterId)}"]`,
        );
        const name = markerNameAt(marker, chapterId, spoilersOn, sceneReached);
        if (label !== null && label.textContent !== name) {
          label.textContent = name;
        }
      }
    };
    canvas.addEventListener("timeline-frame", onFrame);
    onFrame();
    return () => canvas.removeEventListener("timeline-frame", onFrame);
  }, [canvas, getPlayback, layout, spoilersOn]);
  return null;
}
